import { Button } from '@/components/Button';
import type { HealthPlanRequest, OvertimeRequest, RaroInvestRequest } from '../../../../../shared/dpRequests';
import { type StatusMeta, describeRequestStatus } from '../../dpRequests.definitions';
import { useCancelHealthPlan, useCancelOvertime, useCancelRaroInvest } from '../../hooks/data/useDpRequestMutations';
import { HealthPlanSummary, OvertimeSummary, RaroInvestSummary } from '../RequestSummaries';
import { StatusPill } from '../StatusPill';

interface FrameProps {
    meta: StatusMeta;
    cancellable: boolean;
    cancelLabel: string;
    cancelling: boolean;
    onCancel: () => Promise<unknown>;
    children: React.ReactNode;
}

function Frame({ meta, cancellable, cancelLabel, cancelling, onCancel, children }: FrameProps) {
    return (
        <div className="space-y-6">
            <StatusPill label={meta.label} tone={meta.tone} />
            {children}
            {cancellable && (
                <div className="flex justify-end border-t border-raro-gray-200 pt-5">
                    <Button variant="outline" loading={cancelling} onClick={() => void onCancel().catch(() => undefined)}>
                        {cancelLabel}
                    </Button>
                </div>
            )}
        </div>
    );
}

export function MyOvertimeDetail({ request }: { request: OvertimeRequest }) {
    const cancel = useCancelOvertime();
    return (
        <Frame
            meta={describeRequestStatus(request)}
            cancellable={request.status === 'pending_dp'}
            cancelLabel="Cancelar pedido"
            cancelling={cancel.isPending}
            onCancel={() => cancel.mutateAsync(request.id)}
        >
            <OvertimeSummary request={request} />
        </Frame>
    );
}

export function MyRaroInvestDetail({ request }: { request: RaroInvestRequest }) {
    const cancel = useCancelRaroInvest();
    return (
        <Frame
            meta={describeRequestStatus(request)}
            cancellable={request.status === 'pending_dp'}
            cancelLabel="Cancelar pedido"
            cancelling={cancel.isPending}
            onCancel={() => cancel.mutateAsync(request.id)}
        >
            <RaroInvestSummary request={request} />
        </Frame>
    );
}

export function MyHealthPlanDetail({ request }: { request: HealthPlanRequest }) {
    const cancel = useCancelHealthPlan();
    return (
        <Frame
            meta={describeRequestStatus(request)}
            cancellable={request.status === 'pending_dp'}
            cancelLabel="Cancelar pedido"
            cancelling={cancel.isPending}
            onCancel={() => cancel.mutateAsync(request.id)}
        >
            <HealthPlanSummary request={request} />
        </Frame>
    );
}
